// TimeSeriesConfig.tsx - タブ式UI

import React, { useState, useEffect } from 'react';
import { TimeSeriesConfig as TimeSeriesConfigType, GraphConfigProps, TimeSeriesRangeConfig } from '../../../../types/graph-builder.types';
import { createColumnId } from '../../../../types';
import { getIntervalRatioColumns } from '../../../utils/graph-builder-utils';
import * as styles from '../../../styles/components/graph-builder.css.ts';

export const TimeSeriesConfig: React.FC<GraphConfigProps<TimeSeriesConfigType>> = ({
  config,
  dataset,
  onChange
}) => {
  const [activeTab, setActiveTab] = useState<'basic' | 'ranges' | 'advanced'>('basic');
  
  const columns = dataset?.schema?.columns || [];
  
  // X軸として使用可能なカラム（date型、datetime型、int64、float64）
  const timeColumns = columns.filter(col => 
    col.type === 'date' || col.type === 'datetime' || col.type === 'int64' || col.type === 'float64'
  );

  // interval/ratio尺度の数値カラムのみ
  const numericColumns = dataset ? getIntervalRatioColumns(dataset) : [];

  // スマートなデフォルト値の設定（初回のみ）
  useEffect(() => {
    let shouldUpdate = false;
    let updates = { ...config };

    // X軸が未設定で、使用可能な列が1つだけの場合は自動設定
    if (!config.xColumn && timeColumns.length === 1) {
      updates.xColumn = timeColumns[0].id;
      shouldUpdate = true;
    }

    // Y軸が未設定で、数値列が1-3個の場合は自動設定
    if ((!config.yColumns || config.yColumns.length === 0) && 
        numericColumns.length > 0 && numericColumns.length <= 3) {
      updates.yColumns = numericColumns.slice(0, 3).map(col => col.id);
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      onChange(updates);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 初回のみ実行


  // ハンドラー関数
  const handleXColumnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      xColumn: createColumnId(e.target.value)
    });
  };

  const handleYColumnToggle = (columnId: string) => {
    const columnIdTyped = createColumnId(columnId);
    const currentYColumns = config.yColumns || [];
    const isSelected = currentYColumns.includes(columnIdTyped);
    
    if (isSelected) {
      onChange({
        ...config,
        yColumns: currentYColumns.filter(col => col !== columnIdTyped)
      });
    } else {
      onChange({
        ...config,
        yColumns: [...currentYColumns, columnIdTyped]
      });
    }
  };

  const handleSelectAllY = () => {
    onChange({
      ...config,
      yColumns: numericColumns.map(col => col.id)
    });
  };

  const handleClearAllY = () => {
    onChange({
      ...config,
      yColumns: []
    });
  };

  const handleSelectNumericOnly = () => {
    // ratio/interval尺度のみ選択（既に numericColumns がそれ）
    onChange({
      ...config,
      yColumns: numericColumns.slice(0, 5).map(col => col.id) // 最初の5つに制限
    });
  };

  const handleAddRange = () => {
    const newRange: TimeSeriesRangeConfig = {
      upperColumn: createColumnId(''),
      lowerColumn: createColumnId(''),
      label: `Range ${(config.rangeColumns?.length || 0) + 1}`
    };
    onChange({
      ...config,
      rangeColumns: [...(config.rangeColumns || []), newRange]
    });
  };

  const handleRangeChange = (
    index: number,
    field: keyof TimeSeriesRangeConfig,
    value: string
  ) => {
    const rangeColumns = [...(config.rangeColumns || [])];
    if (field === 'upperColumn' || field === 'lowerColumn') {
      rangeColumns[index] = {
        ...rangeColumns[index],
        [field]: value ? createColumnId(value) : createColumnId('')
      };
    } else if (field === 'label' || field === 'color') {
      rangeColumns[index] = {
        ...rangeColumns[index],
        [field]: value
      };
    }
    onChange({
      ...config,
      rangeColumns: rangeColumns
    });
  };

  const handleRemoveRange = (index: number) => {
    const newRangeColumns = config.rangeColumns?.filter((_, i) => i !== index) || [];
    onChange({
      ...config,
      rangeColumns: newRangeColumns.length > 0 ? newRangeColumns : undefined
    });
  };

  const handleRangeOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...config,
      rangeOpacity: parseFloat(e.target.value) / 100 // スライダーは0-100、実際の値は0-1
    });
  };


  if (!dataset || !dataset.schema) {
    return <div>No dataset available</div>;
  }

  return (
    <div className={styles.tabContainer}>
      {/* タブヘッダー */}
      <div className={styles.tabHeader}>
        <button
          className={`${styles.tab} ${activeTab === 'basic' ? styles.active : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          Basic
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'ranges' ? styles.active : ''}`}
          onClick={() => setActiveTab('ranges')}
        >
          Ranges
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'advanced' ? styles.active : ''}`}
          onClick={() => setActiveTab('advanced')}
        >
          Advanced
        </button>
      </div>

      {/* タブコンテンツ */}
      <div className={styles.tabContent}>
        {/* Basic Tab */}
        {activeTab === 'basic' && (
          <div>
            <div className={styles.settingItem}>
              <label className={styles.settingLabel}>X-Axis (Time)</label>
              <select
                value={config.xColumn || ''}
                onChange={handleXColumnChange}
                className={styles.settingSelect}
                data-testid="timeseries-x-axis-select"
              >
                <option value="">Select X-axis column</option>
                {timeColumns.length === 0 ? (
                  <option value="" disabled>No date/datetime/numeric columns available</option>
                ) : (
                  timeColumns.map(col => (
                    <option key={col.id} value={col.id}>
                      {col.name} ({col.type})
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className={styles.settingItem}>
              <label className={styles.settingLabel}>Y-Axis (Values)</label>
              
              {/* クイックアクション */}
              <div className={styles.quickActions}>
                <button 
                  className={styles.chipButton}
                  onClick={handleSelectAllY}
                >
                  Select All
                </button>
                <button 
                  className={styles.chipButton}
                  onClick={handleClearAllY}
                >
                  Clear All
                </button>
                <button 
                  className={styles.chipButton}
                  onClick={handleSelectNumericOnly}
                >
                  Numeric Only
                </button>
              </div>

              {/* チェックボックスリスト */}
              <div className={styles.checkboxScrollList}>
                {numericColumns.length === 0 ? (
                  <div className={styles.noData}>
                    No numeric columns with interval/ratio scale available
                  </div>
                ) : (
                  numericColumns.map(col => (
                    <label key={col.id} className={styles.checkboxItem}>
                      <input
                        type="checkbox"
                        checked={(config.yColumns || []).includes(col.id)}
                        onChange={() => handleYColumnToggle(col.id)}
                      />
                      <span className={styles.columnName}>{col.name}</span>
                      {col.scale?.scale && (
                        <span className={styles.columnScale}>({col.scale.scale})</span>
                      )}
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Ranges Tab */}
        {activeTab === 'ranges' && (
          <div>
            {/* カスタムRange設定 */}
            <div className={styles.settingItem}>
              <label className={styles.settingLabel}>Custom Range Bands</label>
              <button
                onClick={handleAddRange}
                className={styles.primaryButton}
              >
                + Add Range Band
              </button>

              {config.rangeColumns?.map((range, index) => (
                <div key={index} className={styles.rangeItem}>
                  <div className={styles.rangeHeader}>
                    <input
                      type="text"
                      value={range.label || ''}
                      onChange={(e) => handleRangeChange(index, 'label', e.target.value)}
                      placeholder="Range label (e.g., 95% Confidence)"
                      className={styles.rangeLabel}
                    />
                    <button
                      onClick={() => handleRemoveRange(index)}
                      className={styles.dangerButton}
                    >
                      Remove
                    </button>
                  </div>
                  
                  <div className={styles.rangeSelects}>
                    <div className={styles.rangeSelectGroup}>
                      <label className={styles.miniLabel}>Upper Bound</label>
                      <select
                        value={range.upperColumn || ''}
                        onChange={(e) => handleRangeChange(index, 'upperColumn', e.target.value)}
                        className={styles.miniSelect}
                      >
                        <option value="">Select column</option>
                        {numericColumns.map(col => (
                          <option key={col.id} value={col.id}>
                            {col.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className={styles.rangeSelectGroup}>
                      <label className={styles.miniLabel}>Lower Bound</label>
                      <select
                        value={range.lowerColumn || ''}
                        onChange={(e) => handleRangeChange(index, 'lowerColumn', e.target.value)}
                        className={styles.miniSelect}
                      >
                        <option value="">Select column</option>
                        {numericColumns.map(col => (
                          <option key={col.id} value={col.id}>
                            {col.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className={styles.rangeOptions}>
                    <div className={styles.rangeSelectGroup}>
                      <label className={styles.miniLabel}>Color</label>
                      <div className={styles.colorPicker}>
                        <input
                          type="color"
                          value={range.color || '#4285F4'}
                          onChange={(e) => handleRangeChange(index, 'color', e.target.value)}
                          className={styles.colorInput}
                        />
                        <input
                          type="text"
                          value={range.color || '#4285F4'}
                          onChange={(e) => handleRangeChange(index, 'color', e.target.value)}
                          className={styles.colorText}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* 全体の透明度設定 */}
              {config.rangeColumns && config.rangeColumns.length > 0 && (
                <div className={styles.sliderGroup}>
                  <label className={styles.miniLabel}>
                    Range Opacity: {Math.round((config.rangeOpacity || 0.3) * 100)}%
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="50"
                    value={(config.rangeOpacity || 0.3) * 100}
                    onChange={handleRangeOpacityChange}
                    className={styles.slider}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Advanced Tab */}
        {activeTab === 'advanced' && (
          <div>
            <div className={styles.settingItem}>
              <label className={styles.settingLabel}>Display Options</label>
              <div className={styles.checkboxGroup}>
                {/* 将来の拡張用にコメントアウト
                <label className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={true}
                    onChange={() => {}}
                  />
                  <span>Show grid lines</span>
                </label>
                <label className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={true}
                    onChange={() => {}}
                  />
                  <span>Show legend</span>
                </label>
                <label className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={true}
                    onChange={() => {}}
                  />
                  <span>Show data points</span>
                </label>
                <label className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={() => {}}
                  />
                  <span>Interpolate missing values</span>
                </label>
                */}
                <div className={styles.hint}>
                  Display options will be available in a future update.
                </div>
              </div>
            </div>

            <div className={styles.settingItem}>
              <label className={styles.settingLabel}>Chart Title (Optional)</label>
              <input
                type="text"
                value={config.title || ''}
                onChange={(e) => onChange({ ...config, title: e.target.value })}
                placeholder="Enter chart title..."
                className={styles.settingInput}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};